import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Mail } from "lucide-react";
import { getCompanyEmail, type CompanyEmail } from "@/api/email";
import { Hint } from "@/components/email/EmailBadges";
import { usePermissions } from "@/hooks/usePermissions";
import { COMPANY_STATUS, CONTACT_STATUS } from "@/lib/email";
import { campaignPath } from "@/lib/emailRoutes";
import { cn } from "@/lib/utils";

/**
 * The company's email state next to its business status — two separate
 * things: this one is derived from its contacts' campaigns and never
 * changes the business status. Hidden when no contact is in a campaign.
 */
export function CompanyEmailChip({ companyId }: { companyId: number }) {
  const { slug } = useParams<{ slug: string }>();
  const { canViewEmail } = usePermissions();
  const [email, setEmail] = useState<CompanyEmail | null>(null);

  useEffect(() => {
    if (!canViewEmail || !slug) return;
    let cancelled = false;
    getCompanyEmail(slug, companyId)
      .then((data) => !cancelled && setEmail(data))
      .catch(() => !cancelled && setEmail(null));
    return () => {
      cancelled = true;
    };
  }, [slug, companyId, canViewEmail]);

  if (!email?.email_status || !email.memberships.length) return null;
  const tone = COMPANY_STATUS[email.email_status];
  const live = email.memberships.filter((m) => m.status !== "EMAIL_REMOVED");
  const target = live[0] ?? email.memberships[0];

  return (
    <Hint
      content={
        <span className="block space-y-1">
          <span className="block font-medium text-white">Email · {tone.label}</span>
          {email.memberships.slice(0, 6).map((m) => (
            <span key={m.id} className="flex justify-between gap-4">
              <span className="truncate">
                {m.contact_name} — {m.campaign_name}
              </span>
              <span className="shrink-0 opacity-80">{CONTACT_STATUS[m.status].label}</span>
            </span>
          ))}
          {email.memberships.length > 6 && <span className="block opacity-70">+{email.memberships.length - 6} more</span>}
        </span>
      }
    >
      <Link
        to={campaignPath(target.project_slug, target.campaign_id, "leads", { lead: target.id })}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80",
          tone.pill,
        )}
      >
        <Mail className="h-3 w-3" />
        {tone.label}
      </Link>
    </Hint>
  );
}
